<script lang="ts">
  import { page } from '$app/stores';
  import RaceTypeBadge from '$components/race/RaceTypeBadge.svelte';
  import * as HoverCard from '$lib/components/ui/hover-card';
  import {
    executeSearch,
    type SearchInputCodesTypeOption,
    type SearchQueryResult
  } from '$lib/search';

  import TablerSearch from '~icons/tabler/search';
  import BarLoader from '$components/spinners/BarLoader.svelte';
  import { cn } from '$lib/utils';
  import { isVacantBox } from '$utils/race';
  import { formatRaceStartTimeWithDay } from '$utils/time';
  import ResultsBadge from '$components/race/ResultsBadge.svelte';
  import { getRaceLink, selectedRaceId } from '$utils/navigation';
  import { Button } from '$lib/components/ui/button';
  import { MagnifyingGlass } from 'phosphor-svelte';
  import { scrollToActiveRace } from '$components/race/RaceList.svelte';

  let { session } = $page.data;
  export let selectionName: string;
  export let dateTo: string;
  let dateFromValue: string;
  export let trainerName: string;
  export let code: SearchInputCodesTypeOption = 'Thoroughbred';
  export let raceId: string;
  let className: string | undefined | null = undefined;
  export { className as class };

  $: ({ featureFlags } = $page.data);
  $: hasAdvancedSearch = $featureFlags?.includes('advanced_search');

  let loading = false;
  let error = false;
  let results: SearchQueryResult['search']['races'];
  let abortController: AbortController | null = null;

  // strip off any trailing () from the inputName
  // e.g. Acey Deucy (2e) -> Acey Deucy
  let inputName = selectionName.replace(/\s\(\d+\w\)$/, '');

  //   filter out the current race and limit to 5 results
  $: filteredResults = results
    ?.filter((result) => {
      return result?.matchedMarkets.length === 1 && result?.race.id !== raceId;
    })
    .slice(0, 5);
  async function onOpen() {
    // parse dateTo and set dateFrom as 12 months before
    const dateFrom = new Date(dateTo);
    dateFrom.setMonth(dateFrom.getMonth() - 12);
    dateFromValue = dateFrom.toISOString().split('T')[0];

    await search({
      // dates in format 'YYYY-MM-DD'
      dateFrom: dateFromValue,
      dateTo: dateTo
    });
  }

  async function onClose() {
    abortController?.abort();
  }

  async function search(options: { dateFrom?: string; dateTo?: string }) {
    // abort previous request
    abortController?.abort('Abort previous request');
    abortController = new AbortController();
    if (session?.access_token) {
      loading = true;
      try {
        const result = await executeSearch({
          fetch: fetch,
          abortController,
          auth: {
            accessToken: session?.access_token
          },
          variables: {
            input: {
              limit: 6,
              offset: 0,
              codes: [code],
              operator: 'AND',
              dateFrom: options.dateFrom,
              dateTo: options.dateTo,
              items: [
                {
                  operator: 'AND',
                  trainerName: trainerName,
                  name: inputName
                }
              ]
            }
          }
        });
        loading = false;
        if (result.data?.search.races.length > 0) {
          error = false;
          results = result.data.search.races;
        }
        if (result.errors?.length) {
          error = true;
          results = [];
        }
      } catch (error) {
        // ignore abort error (contains 'The operation was aborted')
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.log('error', error);
        error = true;
        loading = false;
        throw error;
      }
    }
  }
</script>

{#if !hasAdvancedSearch}
  <div class={cn(className)}>
    <slot />
  </div>
{:else}
  <HoverCard.Root
    openDelay={1000}
    closeDelay={200}
    onOpenChange={async (open) => {
      open ? await onOpen() : await onClose();
    }}
  >
    <HoverCard.Trigger class={cn(className, '')}>
      <slot />
    </HoverCard.Trigger>
    <HoverCard.Content class="p-1">
      {#if loading}
        <div
          class="flex w-full flex-col place-items-center gap-y-2 p-3 text-sm"
        >
          <p>Loading {inputName} Past Runs...</p>
          <BarLoader color="purple" duration="2s" />
        </div>
      {:else if error}
        <div
          class="flex w-full flex-col place-items-center gap-y-2 p-3 text-sm"
        >
          <p>Error Fetching Past Runs!</p>
        </div>
      {:else if filteredResults}
        <div class="flex place-items-center">
          <div class="flex flex-1 flex-col pl-2">
            <span class="text-md flex w-full font-semibold">{inputName}</span>

            <span class="flex w-full text-sm">Last 5 Runs</span>
            <span class="flex w-full pb-1 text-xs italic">As of {dateTo}</span>
          </div>
          <Button
            size="icon"
            variant="outline"
            href={`/app/racing/search?codes=[${code}]&dateFrom=2020-01-01&dateTo=3030-01-01&items=[{"operator":"AND","trainerName":"${trainerName}","name":"${selectionName}"}]`}
          >
            <MagnifyingGlass class="h-5 w-5" />
          </Button>
        </div>
        <ul class="divide-y">
          {#each filteredResults as result, i}
            {#if result && result.matchedMarkets.length === 1}
              {@const runner = result.race.runners.find(
                (r) => r.id === result?.matchedMarkets[0].selectionId
              )}
              {@const abbreviation =
                result.race.meeting?.type === 'Harness' ? 'D' : 'J'}
              {#if runner}
                <li>
                  <a
                    data-sveltekit-preload-data
                    href={getRaceLink(result.race)}
                    on:click={async () => {
                      selectedRaceId.set(result?.race.id);
                      await $scrollToActiveRace();
                    }}
                    class="flex cursor-pointer items-center bg-gray-100 p-1 px-2 text-xs text-gray-800 hover:bg-gray-200"
                    class:rounded-t={i === 0}
                    class:rounded-b={i === results.length - 1}
                  >
                    <div
                      class="flex flex-row pr-1 text-center {runner.scratchedTime ||
                      isVacantBox(runner)
                        ? 'text-gray-600'
                        : 'text-black'}"
                    >
                      {#if result.race.meeting?.type === 'Greyhound'}
                        {@const regionModifier = ['GBR', 'IRL'].includes(
                          result.race.meeting.location ?? ''
                        )
                          ? '-eu-'
                          : '-'}

                        <div
                          class="h-5 w-5 rounded border border-slate-900/40 selection-g{regionModifier}{runner.number}
                              {runner.scratchedTime || isVacantBox(runner)
                            ? 'opacity-30'
                            : ''}"
                          aria-hidden="true"
                        />
                      {/if}

                      <div class="w-5 self-center font-black">
                        {runner.number}
                        {#if runner.barrier && result.race.meeting?.type === 'Thoroughbred'}
                          ({runner.barrier})
                        {/if}
                      </div>
                    </div>
                    <div class="flex flex-1 flex-col">
                      <div class="flex flex-row items-center gap-x-2">
                        <span class="font-semibold">
                          R{result.race.number}
                          {result.race.meeting?.track} ({result.race.distance}m)
                        </span>
                      </div>
                      {#if result.race.meeting?.type !== 'Greyhound' && runner?.riderName}
                        <!-- this will be only a trainer -->
                        <span class="text-xs italic text-gray-500">
                          {abbreviation}: {runner.riderName}
                        </span>
                      {/if}
                      <span class="text-xs">
                        {formatRaceStartTimeWithDay(result.race.startTime)}
                      </span>
                    </div>

                    <ResultsBadge
                      race={result.race}
                      runnerNumber={runner?.number}
                    />
                  </a>
                </li>
              {/if}
            {/if}
          {/each}
          {#if filteredResults.length === 5}
            <!-- selection has fewer than 5 previous starts -->
            <a
              href={`/app/racing/search?codes=[${code}]&dateFrom=2020-01-01&dateTo=${dateTo}&items=[{"operator":"AND","trainerName":"${trainerName}","name":"${selectionName}"}]`}
              class="flex cursor-pointer items-center bg-gray-100 p-1 px-2 text-xs text-gray-800 hover:bg-gray-200"
            >
              <div class="flex flex-1 flex-col items-center">
                <span class="text-xs">View All</span>
              </div>
            </a>
          {/if}
        </ul>
        {#if filteredResults.length < 5}
          <span class="text-muted-foreground text-[10px] italic">
            No more past runs found in last 12 months
          </span>
        {/if}
      {:else}
        <!-- no results -->
        {#if !loading && !error}
          <div
            class="flex w-full flex-col place-items-center gap-y-2 p-3 text-sm"
          >
            <p>No past runs found in last 12 months</p>
          </div>
        {/if}
      {/if}
    </HoverCard.Content>
  </HoverCard.Root>
{/if}
